import { KeyboardEvent, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { DefaultTheme } from 'styled-components'
import { Wrapper } from './styles'
import RadioInput from 'components/shared/Radio/input'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import { TicketInfo, Zone } from 'store/api-slice/types'
import { ProductStatusPresenter } from 'store/api-slice/hooks'
import { Color } from 'styles/types'
import { PromotionBadge } from 'components/shared/PromotionBadge'
import TicketTypeRadioInfoStub from './components/TicketTypeRadioInfoStub'
import TicketTypeRadioZonesAndPrice from './components/TicketTypeRadioZonesAndPrice'
import { useResolutions } from 'store/resolutions'

interface TicketTypeRadioProps {
    option: string
    id: string
    isChecked: boolean
    label: string
    info?: TicketInfo[] | null
    price: number
    onClick: () => void
    zone: Zone
    numberOfDays: number
    theme?: DefaultTheme
    testId?: string
    isPromotionActive?: boolean
    additionalText?: string
    status?: ProductStatusPresenter
    isBundle?: boolean
    isItNotCombinedWithPromotions?: boolean
}

const TicketTypeRadio = ({
    option,
    id,
    isChecked,
    label,
    info,
    price,
    onClick,
    zone,
    numberOfDays,
    theme,
    testId,
    isPromotionActive,
    additionalText,
    status,
    isBundle,
    isItNotCombinedWithPromotions,
}: TicketTypeRadioProps) => {
    const { isMobile } = useResolutions()

    // TODO: all rules for disabled/semi-disabled status - needs simplification
    const { active: isActive, message } = status || {}
    const isDisabled = !isActive || (!isBundle && !numberOfDays)
    const hasDarkMode = theme?.mode === 'dark'

    // considering all conditions and rules
    const isDefinitelyDisabled = useMemo(() => {
        if (isDisabled) return true
        if (isPromotionActive !== undefined) return !isPromotionActive
        return false
    }, [isPromotionActive, isDisabled])

    const contentColor = useMemo((): { title: Color; price: Color } => {
        if (hasDarkMode) return { title: 'grayscale_200', price: 'primary_500' }
        if (isPromotionActive !== undefined) {
            if (!isPromotionActive)
                return { title: 'grayscale_800', price: 'grayscale_800' }
        }
        if (isDisabled)
            return { title: 'grayscale_500', price: 'grayscale_500' }
        return { title: 'grayscale_900', price: 'secondary_500' }
    }, [hasDarkMode, isPromotionActive, isDisabled])

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) =>
        event.key === 'Enter' && onClick()

    return (
        <Wrapper
            isChecked={isChecked}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            justify="center"
            theme={theme}
            hasInfo={!!info}
            data-testid={testId}
            isDisabled={isDefinitelyDisabled}
        >
            <Box
                direction={isDisabled && isMobile ? 'column' : 'row'}
                align={[numberOfDays > 1 ? 'flex-start' : 'center', 'center']}
                justify="space-between"
                isFullWidth
            >
                {isPromotionActive !== undefined && isBundle && (
                    <PromotionBadge
                        top={[-14, 7]}
                        left={[-10, -10]}
                        element={
                            <FormattedMessage
                                id="global.discount.info"
                                defaultMessage="Promocja"
                            />
                        }
                    />
                )}
                <Box
                    direction="row"
                    align="center"
                    justify="flex-start"
                    gap={12}
                    pt={info ? [0, 12] : 0}
                    mt={[numberOfDays > 1 ? 2 : 0, 0]}
                    isFullWidth={isDisabled && isMobile}
                >
                    <RadioInput
                        option={option}
                        id={id}
                        isChecked={isChecked}
                        isDisabled={isDefinitelyDisabled}
                    />
                    <Box isFullWidth>
                        <Text
                            variant={isChecked ? 'bodyStrong' : 'body'}
                            color={contentColor.title}
                            align={['left', 'center']}
                        >
                            {label}
                        </Text>
                        {isPromotionActive !== undefined && !isPromotionActive && (
                            <Text
                                variant="bodySmallStrong"
                                color="grayscale_700"
                                align={['left', 'center']}
                            >
                                {additionalText}
                            </Text>
                        )}
                        {isItNotCombinedWithPromotions && isBundle && (
                            <Text variant="body12" color="grayscale_400">
                                <FormattedMessage
                                    id="promo.not_combine"
                                    defaultMessage="Nie łączy się z innymi promocjami"
                                />
                            </Text>
                        )}
                    </Box>
                </Box>
                {(!isBundle || (isBundle && !isDisabled)) && (
                    <Box
                        style={
                            numberOfDays && info && !isMobile
                                ? {
                                      margin: '-10px 0',
                                  }
                                : undefined
                        }
                    >
                        <TicketTypeRadioZonesAndPrice
                            price={price}
                            priceColor={contentColor.price}
                            zone={zone}
                            hasFadeOutImages={
                                (isPromotionActive !== undefined &&
                                    !isPromotionActive) ||
                                (isDisabled && !isBundle)
                            }
                            numberOfDays={numberOfDays}
                            hasPriceHidden={isDisabled && !isBundle}
                            hasDarkMode={hasDarkMode}
                        />
                    </Box>
                )}
                {isDisabled && isBundle && (
                    <Text
                        variant="bodySmall"
                        color="grayscale_800"
                        mt={[8, 0]}
                        ml={[36, 0]}
                    >
                        {message}
                    </Text>
                )}
            </Box>
            {!!info && isActive && (
                <TicketTypeRadioInfoStub
                    isChecked={isChecked}
                    info={info}
                    isPromotionActive={isPromotionActive}
                    theme={theme}
                />
            )}
        </Wrapper>
    )
}

export default TicketTypeRadio
